import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Users, ShieldCheck, Sparkles, Star, Search, X, ExternalLink, Pin, DollarSign,
} from "lucide-react";
import CoachPaymentDialog from "../components/CoachPaymentDialog";

const TEAL = "#0D9488";

/** Full-page directory of every verified, opted-in coach on the platform. */
export default function Coaches() {
  const [coaches, setCoaches] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState(null);
  const [paymentTarget, setPaymentTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (specialty) params.specialty = specialty;
    api
      .get("/coaches/directory", { params })
      .then(({ data }) => {
        if (cancelled) return;
        setCoaches(Array.isArray(data?.coaches) ? data.coaches : []);
        setAllowed(Array.isArray(data?.allowed_specialties) ? data.allowed_specialties : []);
      })
      .catch((err) => {
        if (!cancelled) toast.error(formatApiError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [q, specialty]);

  const hasActiveFilter = !!q.trim() || !!specialty;
  const totalLabel = useMemo(() => {
    if (loading) return "Loading…";
    if (coaches.length === 0 && !hasActiveFilter) return "No verified coaches yet.";
    const s = coaches.length === 1 ? "" : "es";
    return `${coaches.length} verified coach${s}`;
  }, [coaches.length, hasActiveFilter, loading]);

  return (
    <AppLayout>
      <PageHero
        eyebrow="Directory"
        title="Coaches"
        subtitle="Every coach on this page is verified by our admin team. Browse profiles, check ratings, and reach out when you find one that fits your goals."
        icon={<Users className="w-8 h-8 text-[#0D9488]" />}
        gradient
      />

      {/* Filter bar */}
      <div className="mb-6 space-y-3" data-testid="coaches-filters">
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, handle, or bio…"
            className="pl-10 h-11 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus-visible:ring-0 focus-visible:border-[#0D9488]"
            data-testid="coaches-search"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              data-testid="coaches-search-clear"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {allowed.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="coaches-specialty-chips">
            {allowed.map((s) => {
              const active = specialty === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSpecialty(active ? null : s)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-mono-num uppercase tracking-wider border transition-colors ${
                    active
                      ? "bg-[#0D9488] text-white border-[#0D9488]"
                      : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-teal-300 hover:text-teal-700"
                  }`}
                  data-testid={`coaches-specialty-${s}`}
                >
                  {s.replace("-", " ")}
                </button>
              );
            })}
          </div>
        )}
        <div className="text-[11px] uppercase tracking-widest font-mono-num text-slate-500" data-testid="coaches-count">
          {totalLabel}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="coaches-loading">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="border border-slate-200 rounded-xl p-6 h-56 animate-pulse bg-white" />
          ))}
        </div>
      ) : coaches.length === 0 ? (
        <Card className="border border-dashed border-slate-200 rounded-xl p-12 text-center bg-white" data-testid="coaches-empty">
          <Users className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          <div className="font-display font-bold text-lg text-slate-900 mb-1">
            {hasActiveFilter ? "No coaches match this filter" : "No verified coaches yet"}
          </div>
          <div className="text-sm text-slate-500 max-w-md mx-auto">
            {hasActiveFilter
              ? "Try clearing the search or picking a different specialty."
              : "Once the admin verifies your first coach they'll appear here for the community to find."}
          </div>
          {hasActiveFilter && (
            <Button
              variant="outline"
              className="mt-4 rounded-full"
              onClick={() => { setQ(""); setSpecialty(null); }}
              data-testid="coaches-reset"
            >
              Reset filters
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="coaches-grid">
          {coaches.map((c) => (
            <CoachCard
              key={c.user_id}
              coach={c}
              onPricingClick={() => setPaymentTarget({ coachId: c.user_id, label: c.name || `@${c.handle}` })}
            />
          ))}
        </div>
      )}

      <CoachPaymentDialog
        open={!!paymentTarget}
        coachId={paymentTarget?.coachId}
        coachLabel={paymentTarget?.label}
        onClose={() => setPaymentTarget(null)}
      />
    </AppLayout>
  );
}

function CoachCard({ coach: c, onPricingClick }) {
  const displayName = c.name || `@${c.handle}`;
  const initials = (c.handle || c.name || "?").slice(0, 2).toUpperCase();
  const accepting = !!c.accepting_clients;
  const profileHref = `/coach/@${c.handle}`;

  return (
    <Card
      className={`relative border rounded-xl p-5 flex flex-col bg-white dark:bg-slate-900 transition-shadow hover:shadow-md ${
        c.is_featured ? "border-amber-300 bg-amber-50/30 dark:bg-amber-950/10" : "border-slate-200 dark:border-slate-700"
      }`}
      data-testid={`coach-card-${c.user_id}`}
    >
      {c.is_featured && (
        <span
          className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-amber-100 text-amber-700"
          data-testid={`coach-card-featured-${c.user_id}`}
        >
          <Pin className="w-2.5 h-2.5" /> Featured
        </span>
      )}

      {/* Header: avatar + name */}
      <div className="flex items-start gap-3 mb-3">
        {c.avatar_url ? (
          <img
            src={c.avatar_url}
            alt={displayName}
            className="w-14 h-14 rounded-full object-cover shrink-0 border border-slate-200"
          />
        ) : (
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 text-white font-bold text-base"
            style={{ background: "linear-gradient(135deg,#5EEAD4 0%,#0D9488 100%)" }}
            aria-hidden
          >
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-base text-slate-900 dark:text-slate-100 truncate" title={displayName}>
            {displayName}
          </div>
          <div className="text-xs text-slate-500 font-mono-num truncate">@{c.handle}</div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-teal-700">
              <ShieldCheck className="w-3 h-3" /> Verified
            </span>
            {accepting ? (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300"
                data-testid={`coach-card-accepting-${c.user_id}`}
              >
                Accepting
              </span>
            ) : (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                data-testid={`coach-card-full-${c.user_id}`}
              >
                Full
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Rating */}
      <div className="flex items-center gap-3 text-xs text-slate-500 mb-3" data-testid={`coach-card-rating-${c.user_id}`}>
        {c.avg_rating !== null && c.rating_count > 0 ? (
          <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
            <Star className="w-3.5 h-3.5 fill-current" />
            {c.avg_rating.toFixed(1)}
            <span className="text-slate-400 font-normal">({c.rating_count})</span>
          </span>
        ) : (
          <span className="italic text-slate-400">No ratings yet</span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 text-slate-500">
          <Users className="w-3 h-3" /> {c.active_client_count} active
        </span>
      </div>

      {/* Bio */}
      <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 leading-relaxed line-clamp-3 min-h-[3.6rem]">
        {c.coach_bio || <span className="italic text-slate-400">This coach hasn't added a bio yet.</span>}
      </p>

      {/* Specialties */}
      {c.coach_specialties && c.coach_specialties.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4" data-testid={`coach-card-specialties-${c.user_id}`}>
          {c.coach_specialties.slice(0, 4).map((s) => (
            <span
              key={s}
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded"
            >
              {s.replace("-", " ")}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="mt-auto flex items-center gap-2">
        <Link to={profileHref} className="flex-1" data-testid={`coach-card-view-${c.user_id}`}>
          <Button
            type="button"
            className="w-full text-white"
            style={{ background: TEAL }}
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            View profile
            <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </Link>
        {c.has_payment && (
          <Button
            type="button"
            variant="outline"
            onClick={onPricingClick}
            className="text-teal-700 border-teal-300 hover:bg-teal-50"
            data-testid={`coach-card-pricing-${c.user_id}`}
            title="See this coach's pricing"
          >
            <DollarSign className="w-4 h-4" />
          </Button>
        )}
      </div>
    </Card>
  );
}
